import { Link } from "react-router"
import type { NewsletterRow, SubscriberRow } from "@client/admin/types"
import { useRows } from "@client/store/entities"
import { ContentNav } from "./content-components"
import { useHasPermission } from "./session-context"

export function ContentPage() {
  const canMutate = useHasPermission("content_mutate")
  const newsletterState = useRows("newsletter")
  const subscriberState = useRows("subscriber")
  const newsletters = (newsletterState.rows ?? []) as NewsletterRow[]
  const subscribers = (subscriberState.rows ?? []) as SubscriberRow[]
  const loading = newsletterState.rows === null || subscriberState.rows === null
  const error = newsletterState.error ?? subscriberState.error

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Content</h1>
        <p className="mt-1 text-sm text-ink/60">Manage newsletters and the people who receive them.</p>
      </div>

      <ContentNav />

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={() => {
              if (newsletterState.error) void newsletterState.retry()
              if (subscriberState.error) void subscriberState.retry()
            }}
            className="mt-3 text-sm font-medium text-red-700 underline"
          >
            Try again
          </button>
        </div>
      ) : (
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-[10px] border border-gold-500 bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-ink/50">Newsletter campaigns</p>
              <p className="mt-2 font-display text-4xl font-bold text-forest-950">
                {loading ? "..." : newsletters.length}
              </p>
            </div>
            <span className="rounded-full bg-cream-100 px-3 py-1 text-xs font-semibold text-forest-700">Campaigns</span>
          </div>
          <p className="mt-4 text-sm text-ink/60">Draft, send, and track updates from the foundation.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/admin/newsletters"
              className="rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-forest-900"
            >
              View newsletters
            </Link>
            {canMutate && (
              <Link
                to="/admin/newsletters/new"
                className="rounded-full border border-gold-500 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
              >
                New newsletter
              </Link>
            )}
          </div>
        </section>

        <section className="rounded-[10px] border border-gold-500 bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-ink/50">Subscribers</p>
              <p className="mt-2 font-display text-4xl font-bold text-forest-950">
                {loading ? "..." : subscribers.filter((row) => row.status === "subscribed").length}
              </p>
            </div>
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">Subscribed</span>
          </div>
          <p className="mt-4 text-sm text-ink/60">Review the mailing list and remove inactive subscribers.</p>
          <div className="mt-6">
            <Link
              to="/admin/subscribers"
              className="rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-forest-900"
            >
              View subscribers
            </Link>
          </div>
        </section>
      </div>
      )}
    </div>
  )
}
